import { useEffect, useRef, useState } from 'react'

interface UseFormDraftOptions<T> {
  /** Stable key per logical form instance. Include the edited entity's id so
   *  creating a new persona vs. editing persona X get separate drafts. */
  key: string
  /** Current form state, composed into a single object. Hook watches this
   *  via JSON-stringify equality and persists on change. */
  values: T
  /** Called once on mount if a draft exists and was not already applied by
   *  the caller's own hydration path (e.g. server-loaded existing record). */
  onRestore?: (draft: T) => void
  /** Skip the hook entirely while this is true — useful while a server
   *  fetch is still hydrating the form, so we don't trample real data
   *  with a stale draft, and don't persist half-loaded state. */
  disabled?: boolean
  /** Milliseconds of settle time before localStorage write. Default 500. */
  debounceMs?: number
  /** Drafts older than this are discarded on read. Default 7 days. */
  maxAgeMs?: number
}

/**
 * Persists the given form `values` to localStorage under `key` and warns the
 * browser before unload when the form is dirty. On mount, if a non-expired
 * draft exists and {@link onRestore} is provided, the draft is passed to it
 * so the caller can dispatch their state setters.
 *
 * <p>Returns a {@link clearDraft} function — call it after a successful
 * submit so the next visit starts clean.
 */
export function useFormDraft<T extends object>(opts: UseFormDraftOptions<T>) {
  const {
    key,
    values,
    onRestore,
    disabled = false,
    debounceMs = 500,
    maxAgeMs = 7 * 24 * 60 * 60 * 1000,
  } = opts

  // Latest non-empty baseline values — used only to detect "form is dirty"
  // for the beforeunload warning. A fresh mount with all-empty fields is
  // considered clean.
  const baselineRef = useRef<string | null>(null)
  // Initial values captured once at first mount — used to detect "user hasn't
  // actually typed anything yet" so we don't pollute localStorage with a no-op
  // draft that would later trigger a pointless "Restored" toast.
  const initialRef = useRef<string | null>(null)
  const [hasRestored, setHasRestored] = useState(false)

  // ── restore ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (disabled || hasRestored) return
    const initialSerialized = JSON.stringify(values)
    if (initialRef.current === null) initialRef.current = initialSerialized
    try {
      const raw = localStorage.getItem(key)
      if (raw) {
        const parsed = JSON.parse(raw) as { savedAt: number; data: T }
        const fresh = parsed && typeof parsed.savedAt === 'number'
          && Date.now() - parsed.savedAt < maxAgeMs
        // Skip restore if the persisted draft is equivalent to what the form
        // already has — this happens when an earlier visit persisted the
        // initial empty state (pre-typing) and the user returns without ever
        // having made meaningful changes. No content to restore = no toast.
        const draftHasContent = fresh && parsed.data
          && JSON.stringify(parsed.data) !== initialSerialized
        if (draftHasContent && onRestore) onRestore(parsed.data)
        else if (!fresh || !draftHasContent) localStorage.removeItem(key)
      }
    } catch {
      // Corrupt JSON — wipe so we don't loop on it.
      try { localStorage.removeItem(key) } catch { /* ignore */ }
    } finally {
      setHasRestored(true)
      baselineRef.current = JSON.stringify(values)
    }
    // Only runs on mount (or when disabled flips false). onRestore is a caller
    // callback that can legitimately change identity; intentionally omitted
    // from deps to avoid re-triggering restore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, key])

  // ── persist (debounced) ────────────────────────────────────────────────
  useEffect(() => {
    if (disabled || !hasRestored) return
    const current = JSON.stringify(values)
    // Don't persist the initial empty state — if user just landed and hasn't
    // typed, there's nothing worth saving. If a prior draft exists at this
    // key from a user who cleared the form, this also cleans it up so the
    // "Restored" toast doesn't re-fire on the next visit.
    if (current === initialRef.current) {
      try { localStorage.removeItem(key) } catch { /* ignore */ }
      return
    }
    const t = window.setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), data: values }))
      } catch { /* quota / private-mode / ignore */ }
    }, debounceMs)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(values), disabled, hasRestored, key, debounceMs])

  // ── beforeunload warning when dirty ────────────────────────────────────
  useEffect(() => {
    if (disabled) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const current = JSON.stringify(values)
      if (baselineRef.current !== null && current !== baselineRef.current) {
        e.preventDefault()
        // Modern browsers ignore returnValue's text and show their own
        // message, but setting it keeps older browsers happy.
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(values), disabled])

  const clearDraft = () => {
    try { localStorage.removeItem(key) } catch { /* ignore */ }
    baselineRef.current = JSON.stringify(values)
  }

  return { clearDraft }
}
